import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  HeaderTitle,
  IconButton,
  LoaderWrapper,
  StyledContainer,
  TableCell,
  TableWrapper,
  TwoColumns,
} from '../../../services/settings/appStyle/global.style';
import { UserContainer } from '../UserV2/user.style';
import {
  Loader,
  Table,
  IconButton as IconButtonWithText,
  Switch,
  Label,
} from '../../../Components/uielements';
// import { useUserContext } from '../../../hooks/UserContext';
import { IntlMessages } from '../../../helper/langHelper';

import {
  customRolesListing,
  toggleCustomRole,
} from '../../../services/bioplus-services/customeRoles';
import { AddPlusIcon, EditIcon } from '../../../Components/icons';
import CreateAndUpdateCustomRoleModal from './modals/CreateAndUpdateCustomRoleModal';
import { useCompany } from '../../../hooks/CompanyContext';

const AdminPartnerCustomRoles = () => {
  //   const userContext = useUserContext();
  //   const companyId = userContext?.company?.companyId?.id;
  const companyContext = useCompany();

  const companyId = companyContext?.company?._id;
  const queryClient = useQueryClient();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const rolesQuery = useQuery(
    ['customRoles', companyId, page, pageSize],
    () => customRolesListing({ companyId, page, pageSize }),
    {
      enabled: !!companyId,
    }
  );

  const rawRoles = rolesQuery?.data;
  const roleTableData = Array.isArray(rawRoles) ? rawRoles : rawRoles?.data ?? [];
  const total = Array.isArray(rawRoles) ? roleTableData.length : rawRoles?.total ?? 0;

  const handleNextPage = () => setPage((value) => value + 1);
  const handlePreviousPage = () => setPage((value) => value - 1);

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setEditRole(null);
  };

  const invalidateRoleQueries = () => {
    queryClient.invalidateQueries('customRoles');
    queryClient.invalidateQueries('allCustomRoles');
  };

  const toggleMutation = useMutation((customRoleId) => toggleCustomRole({ customRoleId }), {
    onMutate: (customRoleId) => setTogglingId(customRoleId),
    onSuccess: async () => {
      invalidateRoleQueries();
    },
    onError: (error) => {
      alert(`Error: ${error?.response?.data?.message || 'Failed to update status.'}`);
    },
    onSettled: () => setTogglingId(null),
  });

  const handleToggleActive = (roleId) => {
    toggleMutation.mutate(roleId);
  };

  const roleAccessType = {
    M: 'Mobile',
    D: 'Desktop',
    B: 'Mobile & Desktop',
  };

  const roleColumns = useMemo(
    () => [
      {
        Header: <IntlMessages id="table.col.label" defaultValue="Label" />,
        accessor: 'label',
        width: '30%',
        Cell: (cellData) => <TableCell>{cellData?.row?.original?.label ?? ''}</TableCell>,
      },
      {
        Header: <IntlMessages id="table.col.roleaccess" defaultValue="Role Access" />,
        accessor: 'roleAccess',
        width: '20%',
        Cell: (cellData) => (
          <TableCell>
            {cellData?.row?.original?.roleAccess
              ? roleAccessType[cellData?.row?.original?.roleAccess]
              : '--'}
          </TableCell>
        ),
      },
      {
        Header: <IntlMessages id="table.col.secure" defaultValue="Secure" />,
        accessor: 'secure',
        width: '20%',
        Cell: (cellData) => (
          <TableCell>
            {cellData?.row?.original?.secure === 1
              ? 'Yes'
              : cellData?.row?.original?.secure === 0
              ? 'No'
              : '--'}
          </TableCell>
        ),
      },
      {
        Header: <IntlMessages id="table.col.status" defaultValue="Status" />,
        accessor: 'isActive',
        width: '20%',
        Cell: (cellData) => {
          const row = cellData?.row?.original;
          return (
            <TableCell>
              <Switch
                handleToggle={() => handleToggleActive(row?._id)}
                checked={!!row?.isActive}
                disabled={toggleMutation.isLoading && togglingId === row?._id}
              />
            </TableCell>
          );
        },
      },
      {
        Header: <IntlMessages id="table.col.actions" />,
        accessor: 'actions',
        isAction: true,
        width: '20%',
      },
    ],
    []
  );

  const actionList = [
    (data) => (
      <IconButton onClick={() => setEditRole(data)}>
        <EditIcon style={{ width: '18px', height: '18px' }} />
      </IconButton>
    ),
  ];

  if (!companyId) {
    return (
      <StyledContainer>
        <UserContainer>
          <HeaderTitle>
            <IntlMessages id="nav.customRoles" defaultValue="Custom Roles" />
          </HeaderTitle>
        </UserContainer>
        <Label style={{ textAlign: 'center', padding: '40px 0', fontSize: '16px' }}>
          <IntlMessages
            id="customRoles.selectCompany"
            defaultValue="Select a entity to view its custom roles."
          />
        </Label>
      </StyledContainer>
    );
  }

  if (rolesQuery.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  if (rolesQuery.isError) {
    return (
      <StyledContainer>
        <IntlMessages id="error.generic" defaultValue="Something went wrong. Please try again." />
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      <UserContainer>
        <HeaderTitle>
          <IntlMessages id="nav.customRoles" defaultValue="Custom Roles" />
        </HeaderTitle>
        <TwoColumns style={{ gap: '15px' }}>
          <IconButtonWithText
            text={<IntlMessages id="button.text.add.role" defaultValue="Add Role" />}
            icon={<AddPlusIcon />}
            onClick={() => setIsAddModalOpen(true)}
          />
        </TwoColumns>
      </UserContainer>

      <TableWrapper>
        <Table
          data={roleTableData}
          columns={roleColumns}
          actions={actionList}
          hasFooter={true}
          page={page}
          pageSize={pageSize}
          prevPage={handlePreviousPage}
          nextPage={handleNextPage}
          total={total}
        />
      </TableWrapper>

      {(isAddModalOpen || editRole) && (
        <CreateAndUpdateCustomRoleModal
          onClose={handleCloseModal}
          companyId={companyId}
          isEdit={!!editRole}
          roleData={editRole}
        />
      )}
    </StyledContainer>
  );
};

export default AdminPartnerCustomRoles;
